if (!customElements.get("cart-coupon")) {
  class CartCoupon extends HTMLElement {
    connectedCallback() {
      if (this.button) return;

      // AJAX-inserted elements initialize once their children are connected.
      const button = this.querySelector("button");
      const input = this.querySelector("input");
      const status = this.querySelector(".cart-coupon-status");
      if (!button || !input || !status) return;

      this.button = button;
      this.input = input;
      this.status = status;
      this.allowMultiple = this.dataset.multiple === "true";
      this.details = this.querySelector(".mini-cart-coupon-dropdown");
      this.details?.addEventListener("toggle", () => {
        if (this.details.open) {
          requestAnimationFrame(() => {
            this.input.focus();
            this.input.select();
          });
        }
      });
      this.button.addEventListener("click", this.submit.bind(this));
      this.input.addEventListener("keydown", this.submit.bind(this));
    }

    async submit(event) {
      if (event instanceof KeyboardEvent && event.key !== "Enter") return;
      event.preventDefault();
      const minicart = document.querySelector("mini-cart");
      if (CartCoupon.submitting || minicart?.checkoutInProgress) return;

      this.hideStatusMessage();
      const inputValue = this.input.value;
      const detailsOpen = this.details?.open;
      // Preserve the existing behavior: only the first entered code is applied.
      const coupon = this.input.value.split(",").map(code => code.trim().toUpperCase()).find(Boolean);
      if (!coupon) return;

      CartCoupon.submitting = true;
      this.setLoading();
      try {
        if (minicart?.enqueueCartMutation) {
          // Register immediately so checkout also waits for this coupon request.
          const quantities = minicart.flushPendingQuantityUpdates();
          await minicart.enqueueCartMutation(async () => {
            await quantities;
            await this.applyCoupon(coupon, inputValue, detailsOpen);
          });
        } else {
          await this.applyCoupon(coupon, inputValue, detailsOpen);
        }
      } catch (error) {
        console.error("[cart-coupon]", error);
        const control = this.getCurrentControl();
        control.input.value = inputValue;
        if (control.details) control.details.open = detailsOpen;
        control.setStatusMessage(cartStrings.couponError, false);
      } finally {
        CartCoupon.submitting = false;
        this.removeLoading();
        this.getCurrentControl().removeLoading();
      }
    }

    getCurrentControl() {
      if (this.isConnected) return this;
      // A pending quantity update may have replaced the original field.
      return document.querySelector(this.classList.contains("minicart-coupon")
        ? "cart-coupon.minicart-coupon" : "cart-coupon.cart-coupon") || this;
    }

    async readResponse(response) {
      const data = await response.json();
      if (!response.ok || !data || data.errors || data.status >= 400) {
        throw new Error(data?.description || data?.message || cartStrings.couponError);
      }
      return data;
    }

    async getCartContents() {
      return this.readCartResponse(await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      }));
    }

    async readCartResponse(response) {
      const cart = await this.readResponse(response);
      if (!Array.isArray(cart.items) ||
          (cart.discount_codes !== undefined && !Array.isArray(cart.discount_codes))) {
        throw new Error(cartStrings.couponError);
      }
      return cart;
    }

    async applyCoupon(coupon, inputValue, detailsOpen) {
      const current = this.getCurrentControl();
      current.input.value = inputValue;
      if (current.details) current.details.open = detailsOpen;
      const cart = await this.getCartContents();
      const existing = this.getExistingCouponCodes(cart);
      const alreadyApplicable = existing.some(discount =>
        discount.code.toUpperCase() === coupon && discount.applicable === true);
      if (alreadyApplicable) {
        this.getCurrentControl().setStatusMessage(cartStrings.couponAlreadyApplied, true);
        return;
      }

      const codes = new Set([coupon]);
      if (this.allowMultiple) {
        existing.forEach(discount => codes.add(discount.code.toUpperCase()));
      }
      const targets = this.getSectionsToRender();
      const response = await this.readCartResponse(await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          discount: [...codes].join(","),
          sections: getUniqueSectionNames(targets).slice(0, 5),
          sections_url: window.location.pathname,
        }),
      }));
      const couponCodes = this.getExistingCouponCodes(response);

      const missing = this.renderSections(response.sections, targets);
      if (missing.length) {
        // Discounts may have applied even if bundled section rendering failed.
        const sections = {};
        const names = getUniqueSectionNames(missing);
        for (let index = 0; index < names.length; index += 5) {
          const params = new URLSearchParams({
            sections: names.slice(index, index + 5).join(","),
            v: String(Date.now()),
          });
          Object.assign(sections, await this.readResponse(await fetch(`${window.location.pathname}?${params}`)));
        }
        if (this.renderSections(sections, missing).length) {
          throw new Error("Die Warenkorbanzeige konnte nicht aktualisiert werden.");
        }
      }

      const applicable = couponCodes.some(discount =>
        discount.code.toUpperCase() === coupon && discount.applicable === true);
      const control = this.getCurrentControl();
      control.setStatusMessage(applicable ? cartStrings.couponSuccess : cartStrings.couponNotApplicable, applicable);
      if (applicable) control.input.value = "";

      publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-coupon", cartData: response });
    }

    renderSections(sections, targets) {
      const missing = [];
      targets.forEach(section => {
        const root = document.getElementById(section.id);
        if (!root) return;
        const html = this.getSectionInnerHTML(sections?.[section.section], section.selector);
        if (html === null) {
          missing.push(section);
          return;
        }
        const descendants = root.querySelectorAll(section.selector);
        const containers = descendants.length ? descendants : [root];
        containers.forEach(container => {
          // Keep live input values, feedback and handlers rather than serializing a clone.
          const control = container.querySelector("cart-coupon");
          const focused = control?.contains(document.activeElement);
          container.innerHTML = html;
          const replacement = container.querySelector("cart-coupon");
          if (control && replacement) {
            replacement.replaceWith(control);
            if (focused) control.input.focus({ preventScroll: true });
          }
        });
      });
      return missing;
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
      if (typeof html !== "string") return null;
      return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML ?? null;
    }

    getSectionsToRender() {
      // Keep the drawer wrappers intact. Targets share section responses;
      // requests are capped at five unique names and missing sections fetched separately.
      return [
        {
          id: "cart-page-title-wrap",
          section: document.getElementById("cart-page-title-wrap")?.dataset.id,
          selector: ".page-title-wrap",
        },
        {
          id: "main-cart-items",
          section: document.getElementById("main-cart-items")?.dataset.id,
          selector: ".cart-items",
        },
        {
          id: "header",
          section: document.getElementById("header")?.dataset.id,
          selector: ".head-slot-cart-link",
        },
        {
          id: "cart-live-region-text",
          section: "cart-live-region-text",
          selector: ".shopify-section",
        },
        {
          id: "main-cart-footer",
          section: document.getElementById("main-cart-footer")?.dataset.id,
          selector: ".cart-footer",
        },
        {
          id: "header-mini-cart-content",
          section: "header-mini-cart",
          selector: "#header-mini-cart-content",
        },
        {
          id: "header-mini-cart-footer",
          section: "header-mini-cart",
          selector: "#header-mini-cart-footer",
        },
        {
          id: "mobile-dock",
          section: document.getElementById("mobile-dock")?.dataset.id,
          selector: ".head-slot-cart-link",
        },
      ].filter(isSectionRenderTargetPresent);
    }

    getExistingCouponCodes(response) {
      return Array.isArray(response?.discount_codes) ? response.discount_codes : [];
    }

    hideStatusMessage() {
      this.status.classList.add("hidden");
      this.status.classList.remove(...["form-status-success", "form-status-error"]);
      this.status.innerHTML = "";
    }

    setStatusMessage(message, success = false) {
      this.status.classList.remove(...["hidden", "form-status-success", "form-status-error"]);
      this.status.classList.add(success ? "form-status-success" : "form-status-error");
      this.status.textContent = message;
    }

    setLoading() {
      this.button.disabled = true;
      this.setAttribute("aria-busy", "true");
      this.button.classList.add("loading");
      this.button.querySelector(".button-overlay-spinner")?.classList.remove("hidden");
    }

    removeLoading() {
      this.button.disabled = false;
      this.removeAttribute("aria-busy");
      this.button.classList.remove("loading");
      this.button.querySelector(".button-overlay-spinner")?.classList.add("hidden");
    }
  }

  customElements.define("cart-coupon", CartCoupon);
}
